import { Box, Paper, Typography, useTheme } from "@mui/material";
import React from "react";
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import bash from "react-syntax-highlighter/dist/cjs/languages/prism/bash";
import csharp from "react-syntax-highlighter/dist/cjs/languages/prism/csharp";
import css from "react-syntax-highlighter/dist/cjs/languages/prism/css";
import javascript from "react-syntax-highlighter/dist/cjs/languages/prism/javascript";
import json from "react-syntax-highlighter/dist/cjs/languages/prism/json";
import jsx from "react-syntax-highlighter/dist/cjs/languages/prism/jsx";
import markup from "react-syntax-highlighter/dist/cjs/languages/prism/markup";
import python from "react-syntax-highlighter/dist/cjs/languages/prism/python";
import sql from "react-syntax-highlighter/dist/cjs/languages/prism/sql";
import tsx from "react-syntax-highlighter/dist/cjs/languages/prism/tsx";
import typescript from "react-syntax-highlighter/dist/cjs/languages/prism/typescript";
import yaml from "react-syntax-highlighter/dist/cjs/languages/prism/yaml";
import CopyToClipboardButton from "../../../common/components/CopyToClipboardButton";
import { codeFontFamily, getCodeSyntaxTheme } from "./codeTokens";

const languages = { javascript, typescript, tsx, jsx, json, csharp, python, sql, bash, markup, css, yaml };
Object.entries(languages).forEach(([name, grammar]) => SyntaxHighlighter.registerLanguage(name, grammar));
const aliases: Record<string, string> = {
  js: "javascript",
  ts: "typescript",
  cs: "csharp",
  "c#": "csharp",
  py: "python",
  sh: "bash",
  shell: "bash",
  html: "markup",
  xml: "markup",
  yml: "yaml",
};

export default function MarkdownCodeBlock({ children }: { children?: React.ReactNode }) {
  const syntaxTheme = getCodeSyntaxTheme(useTheme());
  const element = React.Children.toArray(children).find(React.isValidElement) as
    | React.ReactElement<{ className?: string; children?: React.ReactNode }>
    | undefined;
  const label = /language-([^\s]+)/.exec(element?.props.className ?? "")?.[1] ?? "text";
  const language = aliases[label.toLowerCase()] ?? label.toLowerCase();
  const supported = Object.prototype.hasOwnProperty.call(languages, language);
  const code = String(element?.props.children ?? children ?? "").replace(/\n$/, "");
  return (
    <Paper variant="outlined" sx={{ my: 1, minWidth: 0, maxWidth: "100%", overflow: "hidden", bgcolor: "background.default", borderColor: "divider" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.25,
          py: 0.5,
          bgcolor: "action.hover",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: codeFontFamily, textTransform: "uppercase" }}>
          {label}
        </Typography>
        <CopyToClipboardButton value={code} title="Copy code to clipboard" iconSize={14} edge={false} sx={{ color: "text.secondary", p: 0.25 }} />
      </Box>
      <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
        {supported ? (
          <SyntaxHighlighter
            language={language}
            style={syntaxTheme}
            customStyle={{ margin: 0, padding: "12px 14px", background: "transparent", fontFamily: codeFontFamily, fontSize: "0.78rem", lineHeight: 1.55 }}
            codeTagProps={{ style: { fontFamily: "inherit" } }}
          >
            {code}
          </SyntaxHighlighter>
        ) : (
          <Box component="pre" sx={{ m: 0, p: "12px 14px", fontFamily: codeFontFamily, fontSize: "0.78rem", lineHeight: 1.55, color: "text.primary" }}>
            <code>{code}</code>
          </Box>
        )}
      </Box>
    </Paper>
  );
}
